import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, Pagination, Skeleton, Button } from 'antd';
import { PictureOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { feedService, Feed, PaginatedFeeds } from '../services/feedService';
import { projectService } from '../services/projectService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, SectionCard, EmptyState } from '../components/ui';
import ImageLightbox, { LightboxImage } from '../components/ui/ImageLightbox';
import FeedCard from '../components/feeds/FeedCard';
import './FeedsPage.css';

const FeedsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [feeds, setFeeds] = useState<PaginatedFeeds | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<number | undefined>();
  const [availableProjects, setAvailableProjects] = useState<{ id: number; name: string; status: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewer, setViewer] = useState<{ images: LightboxImage[]; index: number; name: string } | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Every project, not just active ones, so older feeds of finished/canceled projects can be
        // filtered too. The API orders them active → finished → canceled.
        const projects = await projectService.getProjects();
        setAvailableProjects(projects.map(({ id, name, status }) => ({ id, name, status })));
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        toast.error(t('feeds.fetchProjectsError'));
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      setFeeds(await feedService.getAllFeeds(selectedProject, currentPage));
    } catch (error) {
      console.error('Failed to fetch feeds:', error);
      toast.error(t('feeds.fetchFeedsError'));
    } finally {
      setLoading(false);
    }
  }, [selectedProject, currentPage, t]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await feedService.deleteFeed(id);
      toast.success(t('feeds.deleteSuccess'));
      await fetchFeeds();
    } catch (error) {
      console.error('Failed to delete feed:', error);
      toast.error(t('feeds.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const openViewer = (feed: Feed, index: number) =>
    setViewer({
      images: (feed.imageUrls ?? []).map((url, imageIndex) => ({
        full: url,
        thumb: feed.thumbnailUrls?.[imageIndex],
      })),
      index,
      name: [feed.project?.name, new Date(feed.created_at).toISOString().slice(0, 10)].filter(Boolean).join(' '),
    });

  // Posts grouped under Today / Yesterday / date headings, in the order the API returned them
  const groups = useMemo(() => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const result: { key: string; label: string; feeds: Feed[] }[] = [];
    (feeds?.data ?? []).forEach((feed) => {
      const date = new Date(feed.created_at);
      const key = date.toDateString();
      const label =
        key === today
          ? t('feeds.today')
          : key === yesterday
            ? t('feeds.yesterday')
            : date.toLocaleDateString(i18n.language, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              });
      const group = result.find((entry) => entry.key === key);
      if (group) group.feeds.push(feed);
      else result.push({ key, label, feeds: [feed] });
    });
    return result;
  }, [feeds, t, i18n.language]);

  const selectedProjectName = availableProjects.find((project) => project.id === selectedProject)?.name;

  return (
    <MainLayout bare>
      <PageHeader
        title={t('feeds.title')}
        subtitle={
          feeds
            ? selectedProjectName
              ? `${t('dashboard.feedCount', { count: feeds.total })} · ${selectedProjectName}`
              : t('dashboard.feedCount', { count: feeds.total })
            : undefined
        }
        actions={
          <>
            <Select
              style={{ width: 260 }}
              placeholder={t('feeds.filterByProject')}
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedProject}
              onChange={(value) => {
                setSelectedProject(value);
                setCurrentPage(1); // Reset to first page when changing filter
              }}
              options={availableProjects.map((project) => ({
                value: project.id,
                label:
                  project.status === 'active'
                    ? project.name
                    : `${project.name} (${t(`projects.statuses.${project.status}`)})`,
              }))}
            />
            {selectedProject !== undefined && (
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setSelectedProject(undefined);
                  setCurrentPage(1);
                }}
              >
                {t('projects.clearFilters')}
              </Button>
            )}
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchFeeds} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
          </>
        }
      />

      <SectionCard title={t('feeds.allFeeds')} icon={<PictureOutlined />}>
        {loading && !feeds ? (
          <Skeleton active avatar paragraph={{ rows: 4 }} />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<PictureOutlined />}
            text={selectedProject ? t('feeds.noFeedsForProject') : t('feeds.noFeeds')}
          />
        ) : (
          <div className={loading ? 'feeds-list is-loading' : 'feeds-list'}>
            {groups.map((group) => (
              <section key={group.key}>
                <div className="feeds-day">
                  <span className="feeds-day-label">{group.label}</span>
                  <span className="feeds-day-count">{t('dashboard.feedCount', { count: group.feeds.length })}</span>
                </div>
                {group.feeds.map((feed) => (
                  <FeedCard
                    key={feed.id}
                    feed={feed}
                    onOpenImage={(index) => openViewer(feed, index)}
                    onDelete={handleDelete}
                    deleting={deletingId === feed.id}
                  />
                ))}
              </section>
            ))}
          </div>
        )}

        {feeds && feeds.total > feeds.per_page && (
          <div className="feeds-pagination">
            <Pagination
              current={currentPage}
              total={feeds.total}
              pageSize={feeds.per_page}
              onChange={(page) => {
                setCurrentPage(page);
                // The page itself does not scroll any more — the content area does
                document.getElementById('e22-main')?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              showSizeChanger={false}
            />
          </div>
        )}
      </SectionCard>

      {viewer && (
        <ImageLightbox
          images={viewer.images}
          startIndex={viewer.index}
          downloadName={viewer.name}
          onClose={() => setViewer(null)}
          labels={{
            close: t('common.close'),
            previous: t('feeds.previousPhoto'),
            next: t('feeds.nextPhoto'),
            openOriginal: t('feeds.openOriginal'),
            download: t('feeds.downloadPhoto'),
          }}
        />
      )}
    </MainLayout>
  );
};

export default FeedsPage;
